import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiEye, FiDownload, FiFileText } from 'react-icons/fi';
import AnimatedCounter from '../components/AnimatedCounter';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    // Subscribe to real-time updates
    const unsubscribe = subscribeToInvoices();
    
    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedHierarchy]);

  const subscribeToInvoices = () => {
    if (!selectedHierarchy?.outlet?.id) {
      setLoading(false);
      return null;
    }
    
    setLoading(true);
    
    const invoicesRef = collection(db, 'invoices');
    
    // Try with outletId filter and timestamp ordering (matching POS terminal field names)
    try {
      const q = query(
        invoicesRef,
        where('outletId', '==', selectedHierarchy.outlet.id),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      
      // Subscribe to real-time updates
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const invoicesData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              // Map POS terminal fields to web admin expected fields
              // Note: timestamp from Firebase is in UTC - preserved as-is
              createdAt: data.timestamp || data.createdAt,
              total: data.totalAmount || data.total || 0,
            };
          });
          
          setInvoices(invoicesData);
          setLoading(false);
        },
        (error) => {
          // Fallback: Try without orderBy if index is missing
          const fallbackQuery = query(
            invoicesRef,
            where('outletId', '==', selectedHierarchy.outlet.id),
            limit(100)
          );
          
          return onSnapshot(
            fallbackQuery,
            (snapshot) => {
              const invoicesData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  // Note: timestamp from Firebase is in UTC - preserved as-is
                  createdAt: data.timestamp || data.createdAt,
                  total: data.totalAmount || data.total || 0,
                };
              });
              
              setInvoices(invoicesData);
              setLoading(false);
            },
            (fallbackError) => {
              setLoading(false);
            }
          );
        }
      );
      
      return unsubscribe;
    } catch (error) {
      setLoading(false);
      return null;
    }
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.numericId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customerId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'danger';
      case 'cancelled':
        return 'secondary';
      case 'void':
      case 'voided':
        return 'dark';
      default:
        return 'primary';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      let date;
      
      // Handle Firestore Timestamp (already in UTC)
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle ISO string (UTC format like "2025-11-06T10:30:00.000Z")
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      // Handle timestamp number (milliseconds since epoch)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      else {
        return '-';
      }
      
      // Validate date
      if (!date || isNaN(date.getTime())) {
        return '-';
      }
      
      // Format using UTC to avoid timezone conversion
      // This ensures the date displayed matches the date stored in Firebase
      const year = date.getUTCFullYear();
      const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      const day = date.getUTCDate();
      
      return `${month} ${day}, ${year}`;
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return '-';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      let date;
      
      // Handle Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle ISO string
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      // Handle timestamp number
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      else {
        return '-';
      }
      
      // Validate date
      if (!date || isNaN(date.getTime())) {
        return '-';
      }
      
      // Format with UTC time
      const year = date.getUTCFullYear();
      const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      const day = date.getUTCDate();
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      
      return `${month} ${day}, ${year} ${hours}:${minutes} UTC`;
    } catch (error) {
      console.error('Error formatting datetime:', error, timestamp);
      return '-';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${Number(amount).toFixed(2)}`;
  };

  // Calculate dynamic invoice statistics
  const calculateStats = () => {
    const stats = {
      total: invoices.length,
      completed: 0,
      paid: 0,
      pending: 0,
      void: 0,
      totalAmount: 0,
      completedAmount: 0,
      averageAmount: 0,
      statusBreakdown: {}
    };

    invoices.forEach(inv => {
      const amount = Number(inv.total) || 0;
      const status = (inv.status || 'completed').toLowerCase();
      
      // Count by status
      stats.statusBreakdown[status] = (stats.statusBreakdown[status] || 0) + 1;
      
      // Track specific statuses
      if (status === 'completed') stats.completed++;
      if (status === 'paid') stats.paid++;
      if (status === 'pending') stats.pending++;
      if (status === 'void' || status === 'voided') stats.void++;
      
      // Calculate amounts (exclude voided invoices from totals)
      if (status !== 'void' && status !== 'voided') {
        stats.totalAmount += amount;
        if (status === 'completed' || status === 'paid') {
          stats.completedAmount += amount;
        }
      }
    });

    // Calculate average
    const nonVoidedCount = stats.total - stats.void;
    stats.averageAmount = nonVoidedCount > 0 ? stats.totalAmount / nonVoidedCount : 0;

    return stats;
  };

  const stats = calculateStats();

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Invoices</h1>
        <p className="page-subtitle">View and manage invoices</p>
      </div>

      {/* Stats Cards - Dynamically Generated */}
      <div className="row g-3 mb-4">
        {/* Total Invoices */}
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Invoices</p>
                  <h3 className="mb-0">
                    <AnimatedCounter value={stats.total} />
                  </h3>
                  <small className="text-muted">
                    <AnimatedCounter 
                      value={stats.totalAmount} 
                      decimals={2} 
                      prefix="$"
                    />
                    {stats.void > 0 && (
                      <>
                        {' '}(<AnimatedCounter value={stats.void} /> voided)
                      </>
                    )}
                  </small>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--primary-gradient)', width: '48px', height: '48px' }}>
                  <FiFileText className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Completed/Paid */}
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Completed</p>
                  <h3 className="mb-0 text-success">
                    <AnimatedCounter value={stats.completed + stats.paid} />
                  </h3>
                  <small className="text-muted">
                    <AnimatedCounter 
                      value={stats.completedAmount} 
                      decimals={2} 
                      prefix="$"
                    />
                  </small>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--success-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-check-circle text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pending */}
        {stats.pending > 0 && (
          <div className="col-md-3">
            <div className="card hover-lift">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Pending</p>
                    <h3 className="mb-0 text-warning">
                      <AnimatedCounter value={stats.pending} />
                    </h3>
                    <small className="text-muted">
                      <AnimatedCounter 
                        value={invoices
                          .filter(inv => inv.status?.toLowerCase() === 'pending')
                          .reduce((sum, inv) => sum + (Number(inv.total) || 0), 0)}
                        decimals={2}
                        prefix="$"
                      />
                    </small>
                  </div>
                  <div className="stat-card-icon" style={{ background: 'var(--warning-gradient)', width: '48px', height: '48px' }}>
                    <i className="bi bi-clock text-white" style={{ fontSize: '24px' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Void Invoices */}
        {stats.void > 0 && (
          <div className="col-md-3">
            <div className="card hover-lift">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Void Invoices</p>
                    <h3 className="mb-0 text-dark">
                      <AnimatedCounter value={stats.void} />
                    </h3>
                    <small className="text-muted">
                      <AnimatedCounter 
                        value={invoices
                          .filter(inv => inv.status?.toLowerCase() === 'void' || inv.status?.toLowerCase() === 'voided')
                          .reduce((sum, inv) => sum + (Number(inv.total) || 0), 0)}
                        decimals={2}
                        prefix="$"
                      />
                    </small>
                  </div>
                  <div className="stat-card-icon" style={{ background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)', width: '48px', height: '48px' }}>
                    <i className="bi bi-x-circle text-white" style={{ fontSize: '24px' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Total Amount */}
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Revenue</p>
                  <h3 className="mb-0">
                    <AnimatedCounter 
                      value={stats.totalAmount} 
                      decimals={2} 
                      prefix="$"
                    />
                  </h3>
                  {stats.total > 0 && (
                    <small className="text-muted">
                      Avg: <AnimatedCounter 
                        value={stats.averageAmount} 
                        decimals={2} 
                        prefix="$"
                      />
                    </small>
                  )}
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--accent-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-cash-stack text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Status Cards - Dynamic based on data */}
        {Object.entries(stats.statusBreakdown)
          .filter(([status]) => !['completed', 'paid', 'pending', 'void', 'voided'].includes(status))
          .slice(0, 4 - (stats.pending > 0 ? 4 : 3)) // Fill remaining space
          .map(([status, count]) => (
            <div className="col-md-3" key={status}>
              <div className="card hover-lift">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small text-capitalize">{status}</p>
                      <h3 className="mb-0">
                        <AnimatedCounter value={count} />
                      </h3>
                      <small className="text-muted">
                        <AnimatedCounter 
                          value={invoices
                            .filter(inv => inv.status?.toLowerCase() === status)
                            .reduce((sum, inv) => sum + (Number(inv.total) || 0), 0)}
                          decimals={2}
                          prefix="$"
                        />
                      </small>
                    </div>
                    <div className="stat-card-icon" style={{ background: 'var(--info-gradient)', width: '48px', height: '48px' }}>
                      <i className="bi bi-file-earmark text-white" style={{ fontSize: '24px' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Invoices Table */}
      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Invoices</h5>
          <div>
            <button className="btn btn-sm btn-outline-primary me-2">
              <i className="bi bi-funnel me-2"></i>
              Filter
            </button>
            <button className="btn btn-sm btn-primary">
              <i className="bi bi-plus-circle me-2"></i>
              New Invoice
            </button>
          </div>
        </div>
        
        {/* Filter Controls */}
        {!loading && invoices.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-12">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by invoice number, customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {searchTerm && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {filteredInvoices.length} of {invoices.length} invoices
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading invoices...</p>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-5">
              <FiFileText size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm ? 'No invoices match your search.' : 'No invoices found.'}
              </p>
              {searchTerm && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInvoices.map(invoice => (
                      <tr key={invoice.id}>
                        <td className="fw-semibold">
                          {invoice.invoiceNumber || invoice.numericId || `#${invoice.id.slice(0, 8)}`}
                        </td>
                        <td>{invoice.customerName || invoice.customerId || 'Walk-in Customer'}</td>
                        <td>{formatDate(invoice.createdAt)}</td>
                        <td>{formatDate(invoice.dueDate)}</td>
                        <td className="fw-semibold">{formatCurrency(invoice.total)}</td>
                        <td>
                          <span className={`badge bg-${getStatusColor(invoice.status)}`}>
                            {invoice.status || 'completed'}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-2" title="View Invoice">
                            <FiEye />
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" title="Download PDF">
                            <FiDownload />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination */}
          {!loading && filteredInvoices.length > 0 && totalPages > 1 && (
            <div className="card-footer bg-transparent">
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted small">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredInvoices.length)} of {filteredInvoices.length} invoices
                </div>
                
                <nav aria-label="Invoice pagination">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    
                    {/* Page numbers with ellipsis */}
                    {currentPage > 3 && totalPages > 5 && (
                      <>
                        <li className="page-item">
                          <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                        </li>
                        {currentPage > 4 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                      </>
                    )}
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => page >= currentPage - 2 && page <= currentPage + 2)
                      .map(page => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(page)}>
                            {page}
                          </button>
                        </li>
                      ))}
                    
                    {currentPage < totalPages - 2 && totalPages > 5 && (
                      <>
                        {currentPage < totalPages - 3 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        <li className="page-item">
                          <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                        </li>
                      </>
                    )}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </button>
                    </li>
                  </ul>
                </nav>
                
                <div className="d-inline-flex gap-3 text-muted small">
                  {Object.entries(stats.statusBreakdown).map(([status, count]) => (
                    <span key={status}>
                      <span className={`badge bg-${getStatusColor(status)} me-1`} style={{ fontSize: '0.65rem' }}>
                        {count}
                      </span>
                      <span className="text-capitalize">{status}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


